import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import PageSeoHelmet from '../PageSeoHelmet';
import ProductPhotoUploadMulti from '../jewelry/ProductPhotoUploadMulti';
import { serializeBespokePhotos } from '../jewelry/customerPhotoUpload';
import { checkoutAuthHeaders } from '../checkout/checkoutAuthHeaders';
import { useSiteSettings } from '../SiteSettingsContext';
import { useAuth } from '../auth/AuthContext';
import { resolveBespokeJewelryTypes, resolveBespokeOccasions } from '../jewelry/bespokeFormOptions';
import { getCustomJewelryConfig } from '../jewelry/customJewelrySettings';
import { resolveMediaUrl } from '../productMediaUtils';
import './custom-jewelry.css';

const TRUST_ITEMS = [
  { icon: '🎨', title: 'Colour matched', text: 'We design around your outfit photo' },
  { icon: '💬', title: 'Personal consult', text: 'Ideas & quote by WhatsApp or email' },
  { icon: '✋', title: 'Handcrafted', text: 'Jardosi & festive work in our studio' },
];

function ChipGroup({ legend, options, value, onChange, disabled, name }) {
  return (
    <fieldset className="custom-jewelry-fieldset">
      <legend className="custom-jewelry-fieldset__legend">{legend}</legend>
      <div className="custom-jewelry-chips" role="group" aria-label={legend}>
        {options.map((opt) => {
          const selected = value === opt.value;
          return (
            <button
              key={opt.value}
              type="button"
              name={name}
              className={`custom-jewelry-chip${selected ? ' custom-jewelry-chip--selected' : ''}`}
              aria-pressed={selected}
              disabled={disabled}
              onClick={() => onChange(opt.value)}
            >
              {opt.label}
            </button>
          );
        })}
      </div>
    </fieldset>
  );
}

function MultiChipGroup({ legend, hint, options, values, onChange, disabled, name }) {
  const selected = Array.isArray(values) ? values : [];
  const toggle = (optValue) => {
    if (disabled) return;
    if (selected.includes(optValue)) {
      onChange(selected.filter((v) => v !== optValue));
      return;
    }
    onChange([...selected, optValue]);
  };

  return (
    <fieldset className="custom-jewelry-fieldset">
      <legend className="custom-jewelry-fieldset__legend">
        {legend}
        {hint ? <span className="custom-jewelry-fieldset__hint">{hint}</span> : null}
      </legend>
      <div className="custom-jewelry-chips" role="group" aria-label={legend}>
        {options.map((opt) => {
          const isSelected = selected.includes(opt.value);
          return (
            <button
              key={opt.value}
              type="button"
              name={name}
              className={`custom-jewelry-chip${isSelected ? ' custom-jewelry-chip--selected' : ''}`}
              aria-pressed={isSelected}
              disabled={disabled}
              onClick={() => toggle(opt.value)}
            >
              {opt.label}
            </button>
          );
        })}
      </div>
    </fieldset>
  );
}

export default function CustomJewelryPage() {
  const { settings } = useSiteSettings();
  const { ready, isAuthed, displayName, email: accountEmail, phone: accountPhone, username } = useAuth();
  const copy = getCustomJewelryConfig(settings?.store_copy);
  const occasionOptions = resolveBespokeOccasions(settings?.store_copy);
  const jewelryTypeOptions = resolveBespokeJewelryTypes(settings?.store_copy);
  const heroImage = String(copy.hero_image || '').trim();
  const heroImageSrc = heroImage ? resolveMediaUrl(heroImage) : '';

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [occasion, setOccasion] = useState('');
  const [jewelryTypes, setJewelryTypes] = useState([]);
  const [eventDate, setEventDate] = useState('');
  const [dressPhotos, setDressPhotos] = useState([]);
  const [referencePhotos, setReferencePhotos] = useState([]);
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [feedback, setFeedback] = useState({ type: '', text: '' });
  const [submitSuccess, setSubmitSuccess] = useState(null);
  const successRef = useRef(null);

  useEffect(() => {
    if (!ready || !isAuthed) return undefined;
    let cancelled = false;

    const applyContact = (nameVal, emailVal, phoneVal) => {
      if (cancelled) return;
      setName((prev) => prev || nameVal || '');
      setEmail((prev) => prev || emailVal || '');
      setPhone((prev) => prev || phoneVal || '');
    };

    applyContact(displayName || username || '', accountEmail || '', accountPhone || '');

    fetch('/api/checkout_config.php', { headers: checkoutAuthHeaders() })
      .then((r) => r.json())
      .then((d) => {
        if (cancelled || !d?.ok) return;
        const saved = d.saved_shipping && typeof d.saved_shipping === 'object' ? d.saved_shipping : null;
        applyContact(
          displayName || saved?.name || username || '',
          accountEmail || '',
          accountPhone || saved?.phone || ''
        );
      })
      .catch(() => {});

    return () => {
      cancelled = true;
    };
  }, [ready, isAuthed, displayName, accountEmail, accountPhone, username]);

  const resetFormAfterSuccess = () => {
    setOccasion('');
    setJewelryTypes([]);
    setEventDate('');
    setDressPhotos([]);
    setReferencePhotos([]);
    setNotes('');
    if (isAuthed) {
      setName(displayName || username || '');
      setEmail(accountEmail || '');
      setPhone(accountPhone || '');
    } else {
      setName('');
      setEmail('');
      setPhone('');
    }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setFeedback({ type: '', text: '' });
    setSubmitSuccess(null);

    if (!name.trim() || !email.trim() || !phone.trim()) {
      setFeedback({ type: 'danger', text: 'Name, email, and phone are required.' });
      return;
    }
    if (!occasion || jewelryTypes.length === 0) {
      setFeedback({ type: 'danger', text: 'Please select occasion and at least one jewelry type.' });
      return;
    }
    if (dressPhotos.length === 0) {
      setFeedback({ type: 'danger', text: 'Upload at least one photo of your outfit / dress colour so we can match tones.' });
      return;
    }

    setBusy(true);
    try {
      const r = await fetch('/api/bespoke_order.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: name.trim(),
          email: email.trim(),
          phone: phone.trim(),
          occasion,
          jewelry_type: jewelryTypes.join(','),
          event_date: eventDate.trim(),
          dress_photo: serializeBespokePhotos(dressPhotos),
          reference_photo: serializeBespokePhotos(referencePhotos),
          notes: notes.trim(),
        }),
      });
      const d = await r.json();
      if (!r.ok || !d.success) {
        setFeedback({ type: 'danger', text: d.error || 'Could not submit request.' });
        return;
      }
      const successMessage =
        d.message || 'Thank you — we will reach out with design ideas and a quote.';
      const refNum = d.request_number ? String(d.request_number) : '';
      setSubmitSuccess({ message: successMessage, requestNumber: refNum });
      setFeedback({ type: 'success', text: successMessage });
      resetFormAfterSuccess();
      window.setTimeout(() => {
        successRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 80);
    } catch {
      setFeedback({ type: 'danger', text: 'Could not submit. Try WhatsApp or the contact page.' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="store-page custom-jewelry-page">
      <PageSeoHelmet title={copy.seo_title} description={copy.seo_description} />

      <div className="container store-page__container custom-jewelry-page__container">
        <nav className="custom-jewelry-breadcrumb small" aria-label="Breadcrumb">
          <Link to="/shop">Shop</Link>
          <span aria-hidden="true"> / </span>
          <span aria-current="page">Custom jewelry</span>
        </nav>

        <header className={`custom-jewelry-hero${heroImageSrc ? ' custom-jewelry-hero--split' : ''}`}>
          <div className="custom-jewelry-hero__copy">
            <p className="custom-jewelry-hero__eyebrow">{copy.hero_eyebrow}</p>
            <h1 className="custom-jewelry-hero__title">{copy.hero_title}</h1>
            <p className="custom-jewelry-hero__lead">{copy.hero_lead}</p>
          </div>
          {heroImageSrc ? (
            <div className="custom-jewelry-hero__media">
              <img
                src={heroImageSrc}
                alt=""
                className="custom-jewelry-hero__img"
                loading="eager"
                decoding="async"
              />
            </div>
          ) : null}
        </header>

        <ul className="custom-jewelry-trust list-unstyled">
          {TRUST_ITEMS.map((item) => (
            <li key={item.title} className="custom-jewelry-trust__item">
              <span className="custom-jewelry-trust__icon" aria-hidden="true">
                {item.icon}
              </span>
              <span className="custom-jewelry-trust__body">
                <strong>{item.title}</strong>
                <span>{item.text}</span>
              </span>
            </li>
          ))}
        </ul>

        <div className="custom-jewelry-layout">
          <aside className="custom-jewelry-aside">
            <div className="custom-jewelry-aside__card">
              <h2 className="custom-jewelry-aside__title">How it works</h2>
              <ol className="custom-jewelry-steps">
                <li>
                  <span className="custom-jewelry-steps__num">1</span>
                  <span>Tell us your occasion and upload an outfit photo</span>
                </li>
                <li>
                  <span className="custom-jewelry-steps__num">2</span>
                  <span>We share design ideas &amp; a quote on WhatsApp / email</span>
                </li>
                <li>
                  <span className="custom-jewelry-steps__num">3</span>
                  <span>Handcrafted in our studio for your celebration</span>
                </li>
              </ol>
              <div className="custom-jewelry-aside__note">
                <p className="mb-2">
                  <strong>Ready-made with photo lockets?</strong>
                </p>
                <p className="mb-0 small text-muted">
                  Browse{' '}
                  <Link to="/shop/pendants">pendants</Link> and customize on the product page — chain
                  length, photo upload, and more.
                </p>
              </div>
            </div>
          </aside>

          <div className="custom-jewelry-main">
            {submitSuccess ? (
              <div
                ref={successRef}
                className="custom-jewelry-success"
                role="status"
                aria-live="polite"
                tabIndex={-1}
              >
                <div className="custom-jewelry-success__icon" aria-hidden="true">
                  ✓
                </div>
                <h2 className="custom-jewelry-success__title">Request received</h2>
                {submitSuccess.requestNumber ? (
                  <p className="custom-jewelry-success__ref">
                    Reference <strong>#{submitSuccess.requestNumber}</strong>
                  </p>
                ) : null}
                <p className="custom-jewelry-success__lead">{submitSuccess.message}</p>
                <p className="custom-jewelry-success__fine small text-muted mb-0">
                  We will reply on WhatsApp or email with design ideas and a quote. No payment is needed now.
                </p>
                <div className="custom-jewelry-success__actions">
                  {isAuthed ? (
                    <Link to="/account?tab=custom-designs" className="btn btn-primary rounded-pill">
                      View my custom requests
                    </Link>
                  ) : (
                    <Link to="/login?next=/account%3Ftab%3Dcustom-designs" className="btn btn-outline-primary rounded-pill">
                      Sign in to track requests
                    </Link>
                  )}
                  <button
                    type="button"
                    className="btn btn-outline-secondary rounded-pill"
                    onClick={() => {
                      setSubmitSuccess(null);
                      setFeedback({ type: '', text: '' });
                    }}
                  >
                    Submit another request
                  </button>
                </div>
              </div>
            ) : (
              <form className="custom-jewelry-form" onSubmit={onSubmit} noValidate>
              {feedback.text && feedback.type === 'danger' ? (
                <div className={`alert alert-${feedback.type} custom-jewelry-alert`} role="alert">
                  {feedback.text}
                </div>
              ) : null}

              <section className="custom-jewelry-section">
                <h2 className="custom-jewelry-section__title">Your details</h2>
                {isAuthed ? (
                  <p className="custom-jewelry-account-note small text-muted mb-3">
                    Signed in as <strong>{displayName || username}</strong> — your details are filled in below.
                    Edit anything that should be different for this request.
                  </p>
                ) : null}
                <div className="row g-3">
                  <div className="col-md-6">
                    <label className="form-label custom-jewelry-label" htmlFor="bespoke-name">
                      Your name <span className="text-danger">*</span>
                    </label>
                    <input
                      id="bespoke-name"
                      className="form-control custom-jewelry-input"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      required
                      disabled={busy}
                      autoComplete="name"
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label custom-jewelry-label" htmlFor="bespoke-phone">
                      Phone / WhatsApp <span className="text-danger">*</span>
                    </label>
                    <input
                      id="bespoke-phone"
                      className="form-control custom-jewelry-input"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      required
                      disabled={busy}
                      autoComplete="tel"
                    />
                  </div>
                  <div className="col-12">
                    <label className="form-label custom-jewelry-label" htmlFor="bespoke-email">
                      Email <span className="text-danger">*</span>
                    </label>
                    <input
                      id="bespoke-email"
                      type="email"
                      className="form-control custom-jewelry-input"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      disabled={busy}
                      autoComplete="email"
                    />
                  </div>
                </div>
              </section>

              <section className="custom-jewelry-section">
                <h2 className="custom-jewelry-section__title">Your event</h2>
                <ChipGroup
                  legend="Occasion *"
                  name="occasion"
                  options={occasionOptions}
                  value={occasion}
                  onChange={setOccasion}
                  disabled={busy}
                />
                <MultiChipGroup
                  legend="Jewelry type *"
                  hint="Select all that apply"
                  name="jewelry_type"
                  options={jewelryTypeOptions}
                  values={jewelryTypes}
                  onChange={setJewelryTypes}
                  disabled={busy}
                />
                <div className="mt-3">
                  <label className="form-label custom-jewelry-label" htmlFor="bespoke-date">
                    Event date <span className="text-muted fw-normal">(optional)</span>
                  </label>
                  <input
                    id="bespoke-date"
                    type="date"
                    className="form-control custom-jewelry-input custom-jewelry-input--date"
                    value={eventDate}
                    onChange={(e) => setEventDate(e.target.value)}
                    disabled={busy}
                  />
                </div>
              </section>

              <section className="custom-jewelry-section">
                <h2 className="custom-jewelry-section__title">Photos &amp; inspiration</h2>
                <div className="custom-jewelry-uploads">
                  <ProductPhotoUploadMulti
                    id="bespoke-dress-photos"
                    label="Outfit / dress photos"
                    hint="Upload your saree, lehenga, or outfit — add multiple angles or close-ups of the fabric colour."
                    values={dressPhotos}
                    onChange={setDressPhotos}
                    required
                    disabled={busy}
                  />
                  <ProductPhotoUploadMulti
                    id="bespoke-ref-photos"
                    label="Reference / inspiration"
                    hint="Pinterest screenshots, previous jewelry, or looks you love (optional)."
                    values={referencePhotos}
                    onChange={setReferencePhotos}
                    disabled={busy}
                  />
                </div>
                <div className="mt-3">
                  <label className="form-label custom-jewelry-label" htmlFor="bespoke-notes">
                    Design notes
                  </label>
                  <textarea
                    id="bespoke-notes"
                    className="form-control custom-jewelry-input"
                    rows={4}
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Colours, jardosi style, budget range, pieces needed…"
                    disabled={busy}
                  />
                </div>
              </section>

              <div className="custom-jewelry-submit">
                <button type="submit" className="btn btn-primary btn-lg custom-jewelry-submit__btn" disabled={busy}>
                  {busy ? 'Sending request…' : 'Request custom design'}
                </button>
                <p className="custom-jewelry-submit__fine small text-muted mb-0">
                  No payment now — we&apos;ll reply with ideas and a quote first.
                </p>
              </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
